import type { FC, NamedExoticComponent, ReactElement, ReactNode } from "react"
import { memo } from "react"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import isNull from "lodash-es/isNull"
import { Outlet } from "react-router-dom"

import { ParticipantHeader, ViewAsBanner } from "@/3514/components"
import {
    type TScreenBreakpoints,
    useDefaultStylesReset,
    useHtmlScrollbarGutter,
    useInjectReducer,
    useIsFakeParticipant,
    useScreenBreakpoints
} from "@/3514/hooks"
import { PARTICIPANT_CHAPTER_REDUCER_NAME, participantChapterReducer } from "@/3514/pages/participant.chapter"
import { type IAppState, useAppSelector } from "@/3514/store/app"
import { buildCssClassName } from "@/3514/utils"

type TProps = { children?: ReactNode; withHtmlScrollBarGutter?: boolean }

const LAYOUT_CLASS_NAME: string = "p-layout"

const classes = {
    main: (): string => buildCssClassName("relative", LAYOUT_CLASS_NAME),
    container: ({ isFakeParticipant }: { isFakeParticipant: boolean }): string =>
        buildCssClassName("flex flex-col flex-1", isFakeParticipant && "top-[70px] md:top-0 relative")
} as const

const Layout: FC<TProps> = ({ children = null, withHtmlScrollBarGutter = false }: TProps): ReactElement => {
    useInjectReducer(PARTICIPANT_CHAPTER_REDUCER_NAME, participantChapterReducer, false)

    useHtmlScrollbarGutter({ isEnabled: withHtmlScrollBarGutter })

    useDefaultStylesReset(LAYOUT_CLASS_NAME)

    const { isOpenSidebar }: IAppState = useAppSelector()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMotionReduced: boolean = useReducedMotion()

    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.main
                animate={{ marginLeft: breakpoints.xs || breakpoints.sm ? 0 : isOpenSidebar ? 250 : 80 }}
                transition={{
                    duration: isMotionReduced || Boolean(breakpoints.xs || breakpoints.sm) ? 0 : 0.4,
                    type: "tween",
                    ease: "easeInOut"
                }}
                className={classes.main()}
            >
                {isFakeParticipant && <ViewAsBanner />}

                <div className={classes.container({ isFakeParticipant })}>
                    <ParticipantHeader />

                    {isNull(children) ? <Outlet /> : children}
                </div>
            </motion.main>
        </AnimatePresence>
    )
}

Layout.displayName = "ParticipantLayout"

const MemoizedLayout: NamedExoticComponent<TProps> = memo(Layout)

export { MemoizedLayout as ParticipantLayout }
